<template>
  <div id="app">
    <div
      v-if="!isLoginRoute() && isLoggedin"
      id="header"
    >
      <AppPrimaryHeader />
    </div>
    <div :class="[!isLoginRoute() ? 'wrapper' : 'login']">
      <div
        v-if="!isLoginRoute() && isLoggedin"
        class="side-navbar-section"
      >
        <AppSidebar id="side-navbar" />
      </div>
      <div class="main-content">
        <router-view v-slot="{ Component, route }">
          <transition name="fade">
            <component
              :is="Component"
              v-if="route.matched.length > 0"
              :key="route.name"
            />
          </transition>
        </router-view>
      </div>
    </div>
    <Modal
      :is-closable="false"
      :show="isLoggedin && displaySessionWarning"
      :container-style="{ maxWidth: '360px' }"
      body-class="atb-border-none"
      footer-class="atb-flex atb-flex-row atb-justify-end atb-gap-8"
      :dialog-style="{ zIndex: '1000000' }"
    >
      <template #header> {{ $t('oops') }} </template>
      <template #body>
        <div class="atb-flex atb-flex-row atb-gap-4">
          <span>
            {{ $t('sessionWarningFirstPhrase') }}
            <VueCountdown
              :time="warningTimeInMs - sessionTimeoutOffsetInMs"
              :emit-events="false"
            >
              <template v-slot="{ minutes, seconds }">
                {{ minutes }} minute {{ seconds }} seconds.
              </template>
            </VueCountdown>
            {{ $t('sessionWarningSecondPhrase') }}</span
          >
        </div>
      </template>
      <template #footer>
        <Button
          motif="basic"
          @click="sessionLogoutHandler"
        >
          {{ $t('logout') }}</Button
        >
        <Button @click="sessionContinueHandler">{{ $t('yes') }}</Button>
      </template>
    </Modal>

    <Modal
      :is-closable="false"
      :show="isAtbAuthorizationError"
      :container-style="{ maxWidth: '800px' }"
      body-class="atb-border-none"
      footer-class="atb-flex atb-flex-row atb-justify-end atb-gap-8"
      :dialog-style="{ zIndex: '1000000' }"
    >
      <template #header>ATB Error</template>
      <template #body>
        <div
          role="alert"
          class="eds-alert alert-error alert-gray"
          style="width: 700px"
        >
          <div class="alert-content">
            <span class="alert-title">Authorization Error:</span>
            <div class="atb-flex atb-flex-row atb-justify-end atb-gap-4">
              <span>
                {{ $t('authorizationError') }}
                <VueCountdown
                  :time="errorDisplayTimeinMs"
                  :emit-events="false"
                  @end="sessionLogoutHandler"
                >
                  <template v-slot="{ seconds }">
                    {{ seconds }} seconds.
                  </template>
                </VueCountdown>
              </span>
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <Button
          motif="basic"
          @click="sessionLogoutHandler"
        >
          {{ $t('logout') }}</Button
        >
      </template>
    </Modal>

    <Alert
      :show="isAlertOpen"
      :motif="motif"
      shadow
      border
      compact
      class="floating"
      @close="alertStore.changeAlertVisibility()"
    >
      <template #title> {{ title + ': ' }} </template>
      <template #message> {{ message }} </template>
    </Alert>
    <AlertPopup
      @confirm="alertStore.handleConfirm"
      @cancel="alertStore.handleCancel"
    />
  </div>
</template>

<script lang="ts">
  import '@atb-ui/eds-vue/dist/index.css';
  import { computed, defineComponent, onMounted, onUnmounted } from 'vue';
  import { storeToRefs } from 'pinia';
  import AppPrimaryHeader from '@/app/shared/components/AppPrimaryHeader.vue';
  import AppSidebar from '@/app/shared/components/AppSidebar.vue';
  import { AuthStatus, useAuthStore } from '@/app/shared/stores/auth-store';
  import { useAlertStore } from '@/app/shared/stores/alert-store';
  import { useFeatureStore } from '@/app/shared/stores/feature-store';
  import Modal from '@atb-ui/eds-vue/src/components/Modal.vue';
  import Alert from '@atb-ui/eds-vue/src/components/Alert.vue';
  import Button from '@atb-ui/eds-vue/src/components/Button.vue';
  import { useRoute } from 'vue-router';
  import VueCountdown from '@chenfengyuan/vue-countdown';
  import useSessionTracker from '@/app/shared/hooks/use-session-tracker';
  import { AuthState } from '@okta/okta-auth-js';
  import { oktaAuth } from '@/app/shared/okta/auth';
  import AlertPopup from '@/app/shared/components/AlertPopupModal.vue';
  import router from './routes';
  import useActivityTracker from './app/shared/hooks/use-activity-tracker';

  export default defineComponent({
    name: 'App',
    components: {
      Alert,
      AppPrimaryHeader,
      AppSidebar,
      Modal,
      Button,
      VueCountdown,
      AlertPopup,
    },
    setup: () => {
      /*
        This is required because of a synchronization problem between the timeouts of use-session-tracker and vuecountdown,
        if this is not set the session gets restarted before the visual timer reaches 
      */
      const sessionTimeoutOffsetInMs = 2000;

      const route = useRoute();
      const authStore = useAuthStore();
      const alertStore = useAlertStore();
      const { isAlertOpen, message, motif, title } = storeToRefs(alertStore);

      const sessionTracker = useSessionTracker();
      const { displaySessionWarning, warningTimeInMs } =
        storeToRefs(sessionTracker);
      const { sessionContinueHandler, sessionLogoutHandler } = sessionTracker;

      const isLoggedin = computed(() => {
        return authStore.isAuthenticated;
      });

      const isAtbAuthorizationError = computed(() => {
        return authStore.isAtbAuthorizationError;
      });

      oktaAuth.authStateManager.subscribe(async (authState: AuthState) => {
        await authStore.updateLoginInfo(authState);
        if (authState.isAuthenticated) {
          const featureStore = useFeatureStore();
          await featureStore.initializeFeatureStore();
        }
      });

      const errorDisplayTimeinMs = authStore.errorDisplayTimeinMs;

      const isLoginRoute = () => {
        const routeName = route.name || '';
        return routeName.toString() === 'login';
      };

      window.onbeforeunload = function (e) {
        e = e || window.event;

        if (isLoggedin.value) {
          if (e) {
            e.returnValue = 'Changes you made may not be saved. ';
          }

          e.preventDefault();
          return 'Changes you made may not be saved. ';
        } else {
          return null;
        }
      };

      const { listenForActivity, stopListeningForActivity } =
        useActivityTracker();

      onMounted(() => {
        listenForActivity();
      });

      onUnmounted(() => {
        stopListeningForActivity();
      });

      return {
        sessionTimeoutOffsetInMs,
        displaySessionWarning,
        isLoggedin,
        isAtbAuthorizationError,
        errorDisplayTimeinMs,
        warningTimeInMs,
        isLoginRoute,
        isAlertOpen,
        sessionContinueHandler,
        sessionLogoutHandler,
        alertStore,
        message,
        motif,
        title,
      };
    },
  });
</script>
<style scoped>
  .floating {
    position: absolute;
    top: 5.5em;
    right: 1em;
    z-index: 999999;
    max-width: 25%;
  }
</style>
